import * as React from 'react';
import { cn } from '@/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';

// =============================================
// Z-INDEX LAYER SYSTEM FOR NESTED MODALS
// =============================================
// Base z-index starts at 40 (--z-modal), each nested modal adds 2 layers
const DIALOG_BASE_Z_INDEX = 40;
const DIALOG_Z_INDEX_INCREMENT = 2;

// Context to track modal nesting depth
const DialogDepthContext = React.createContext<number>(0);

/**
 * Hook to get the current dialog depth and calculate z-index values
 */
const useDialogDepth = () => {
  const depth = React.useContext(DialogDepthContext);
  const baseZ = DIALOG_BASE_Z_INDEX + depth * DIALOG_Z_INDEX_INCREMENT;
  return {
    depth,
    overlayZIndex: baseZ,
    contentZIndex: baseZ + 1,
  };
};

// Custom Dialog Root that increments depth for children
const Dialog = ({ children, ...props }: DialogPrimitive.DialogProps) => {
  const parentDepth = React.useContext(DialogDepthContext);
  const newDepth = parentDepth + 1;

  return (
    <DialogPrimitive.Root {...props}>
      <DialogDepthContext.Provider value={newDepth}>{children}</DialogDepthContext.Provider>
    </DialogPrimitive.Root>
  );
};

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, style, ...props }, ref) => {
  const { overlayZIndex } = useDialogDepth();

  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 bg-black/25 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className
      )}
      style={{ zIndex: overlayZIndex, ...style }}
      {...props}
    />
  );
});
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface IDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  closeClassName?: string;
  overlayClassName?: string;
  showClose?: boolean;
}

// SSR-safe hook to detect if current client pathname contains '/admin'
function useIsAdminPath() {
  const [isAdminPath, setIsAdminPath] = React.useState(false);
  React.useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        setIsAdminPath(window.location.pathname.includes('/admin'));
      }
    } catch (e) {
      // ignore
    }
  }, []);
  return isAdminPath;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  IDialogContentProps
>(
  (
    { className, children, closeClassName, overlayClassName, showClose = true, style, ...props },
    ref
  ) => {
    const isAdminPath = useIsAdminPath();
    const { contentZIndex } = useDialogDepth();

    return (
      <DialogPortal>
        <DialogOverlay className={overlayClassName} />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'fixed left-[50%] top-[80px] grid w-full max-w-lg translate-x-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[10%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[10%] sm:rounded-lg',
            className,
            isAdminPath && 'modal-atlassian'
          )}
          style={{ zIndex: contentZIndex, ...style }}
          {...props}
        >
          {children}
          {showClose && (
            <DialogPrimitive.Close
              className={cn(
                'absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none',
                closeClassName
              )}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_13577_1501)">
                  <path
                    d="M11.0327 8L15.5814 3.45136C16.1395 2.89318 16.1395 1.98818 15.5814 1.42955L14.5705 0.418636C14.0123 -0.139545 13.1073 -0.139545 12.5486 0.418636L8 4.96727L3.45136 0.418636C2.89318 -0.139545 1.98818 -0.139545 1.42955 0.418636L0.418636 1.42955C-0.139545 1.98773 -0.139545 2.89273 0.418636 3.45136L4.96727 8L0.418636 12.5486C-0.139545 13.1068 -0.139545 14.0118 0.418636 14.5705L1.42955 15.5814C1.98773 16.1395 2.89318 16.1395 3.45136 15.5814L8 11.0327L12.5486 15.5814C13.1068 16.1395 14.0123 16.1395 14.5705 15.5814L15.5814 14.5705C16.1395 14.0123 16.1395 13.1073 15.5814 12.5486L11.0327 8Z"
                    className="fill-primary "
                  />
                </g>
                <defs>
                  <clipPath id="clip0_13577_1501">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>

              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  const isAdminPath = useIsAdminPath();
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        'text-lg font-semibold  leading-none tracking-tight',
        className,
        isAdminPath && 'modal-atlassian'
      )}
      {...props}
    />
  );
});
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  const isAdminPath = useIsAdminPath();
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn('text-sm text-muted-foreground', className, isAdminPath && 'modal-atlassian')}
      {...props}
    />
  );
});
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
